import UsagePage from "./components/UsagePage";
import StoryFormLoading from './components/common/StoryFormLoading';
import React, { lazy, Suspense } from 'react';
import './App.css';

import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import HomePage from './components/HomePage';

import Header from './components/Header';
import { getAllRoutePaths } from './util/routeConfig';
import { getAllLocalizedRoutes } from './util/languageRoutes';
import { AuthProvider } from './contexts/AuthContext';
import TermsAcceptanceGate from './components/TermsAcceptanceGate';
import { LanguageProvider, SUPPORTED_LANGUAGES } from './contexts/LanguageContext';

import { Provider } from 'react-redux';
import { store, persistor } from './store';

import { PersistGate } from 'redux-persist/integration/react';
import RouteChangeTracker from './components/RouteChangeTracker';
import { QueryProvider } from './providers/QueryProvider';
import { WebSocketProviderV2 } from './contexts/WebSocketContextV2';
import { RealtimeProvider } from './contexts/RealtimeContext';
import { DashboardShellProvider } from './contexts/DashboardShellContext';
import DashboardContentFrame from './components/DashboardContentFrame';
import { CreateStoryRouteLoading } from './components/common/CreateStoryLoadingShell';
import { useRestoreStudioMode } from './components/createModePreference';
import LegacyStudioRedirect from './components/LegacyStudioRedirect';
import LegacyVideoRedirect from './components/LegacyVideoRedirect';
import LegacyMusicalRedirect from './components/LegacyMusicalRedirect';
import StudioRouteLoading from "./components/studio/StudioRouteLoading";
import LoadingBookOverlay from './components/common/LoadingBookOverlay';
import PublicFooter from './components/common/PublicFooter';
import { isDashboardPath } from './util/dashboardRoutes';

import AffiliateAttributionListener from './components/AffiliateAttributionListener';

import { STUDIO_POC_ENABLED, WEB_RELEASE_FLAGS } from './config/releaseFlags';

import NotFoundPage from './components/NotFoundPage';

const StoryScreen = lazy(() => import('./components/StoryScreen'));
const DeleteConfirm = lazy(() => import('./components/pages/DeleteConfirm'));
const DeleteProfile = lazy(() => import('./components/pages/DeleteProfile'));
const Privacy = lazy(() => import('./components/pages/Privacy'));
const Terms = lazy(() => import('./components/pages/Terms'));
const FAQ = lazy(() => import('./components/pages/FAQ'));
const FAQQuestion = lazy(() => import('./components/pages/FAQQuestion'));
const StylePage = lazy(() => import('./components/pages/StylePage'));
const StoryDetailsPage = lazy(() => import('./components/pages/StoryDetailsPage'));
const VideoPage = lazy(() => import('./components/VideoPage'));
const VideoExplorePage = lazy(() => import('./components/VideoExplorePage'));
const StoryGeneratorPage = lazy(() => import('./components/StoryGeneratorPage'));
const StoryVideoPage = lazy(() => import('./components/StoryVideoPage'));
const AuthCallback = lazy(() => import('./components/AuthCallback'));
const TikTokAuthCallback = lazy(() => import('./components/TikTokAuthCallback'));
const PaymentCallback = lazy(() => import('./components/PaymentCallback'));
const SocialCallback = lazy(() => import('./components/SocialCallback'));
const MyStories = lazy(() => import('./components/MyStories'));
const SignInPage = lazy(() => import('./components/SignInPage'));
const EditStory = lazy(() => import('./components/EditStory'));
const CreateStoryPage = lazy(() => import('./components/CreateStoryPage'));
const StudioPage = lazy(() => import('./components/studio/StudioPage'));
const StudioPendingStoryPreview = lazy(() => import('./components/studio/StudioPendingStoryPreview'));
const CreateStoryDetailPage = lazy(() => import('./components/CreateStoryDetailPage'));
const CreateNextChapterPage = lazy(() => import('./components/CreateNextChapterPage'));
const StoryPreviewPage = lazy(() => import('./components/StoryPreviewPage'));
const SettingsPage = lazy(() => import('./components/SettingsPage'));
const NotificationsPage = lazy(() => import('./components/NotificationsPage'));
const SupportPage = lazy(() => import('./components/SupportPage'));
const PaymentPage = lazy(() => import('./components/PaymentPage'));
const AccountBillingPage = lazy(() => import('./components/AccountBillingPage'));
const BookBuilder = lazy(() => import('./components/BookBuilderEntry'));
const GambitPage = lazy(() => import('./components/GambitPage'));
const VoiceDesignPreview = process.env.NODE_ENV === 'development'
  ? lazy(() => import('./components/marketing/VoiceDesignPreview'))
  : null;
const BooksPage = lazy(() => import('./components/BooksPage'));
const BookPreview = lazy(() => import('./components/BookPreview'));
const DownloadPage = lazy(() => import('./components/DownloadPage'));
const ShareDetailsPage = lazy(() => import('./components/ShareDetailsPage'));
const SocialAccountsPage = lazy(() => import('./components/SocialAccountsPage'));
const PublishingResultsPage = lazy(() => import('./components/PublishingResultsPage'));
const MyOrders = lazy(() => import('./components/MyOrders'));
const OrderDetail = lazy(() => import('./components/OrderDetail'));
const CharactersPage = lazy(() => import('./components/CharactersPage'));
const CreateCharacterPage = lazy(() => import('./components/CreateCharacterPage'));
const VoicesPage = lazy(() => import('./components/VoicesPage'));
const CreateVoicePage = lazy(() => import('./components/CreateVoicePage'));
const BlogPage = lazy(() => import('./components/BlogPage'));
const BlogPostPage = lazy(() => import('./components/BlogPostPage'));
const NarratorDetailPage = lazy(() => import('./components/pages/NarratorDetailPage'));
const LanguageDetailPage = lazy(() => import('./components/pages/LanguageDetailPage'));
const MoralDetailPage = lazy(() => import('./components/pages/MoralDetailPage'));
const ExploreNarratorsPage = lazy(() => import('./components/ExploreNarratorsPage'));
const ExploreStoriesPage = lazy(() => import('./components/ExploreStoriesPage'));
const ExploreMoralsPage = lazy(() => import('./components/ExploreMoralsPage'));
const ExploreLanguagesPage = lazy(() => import('./components/ExploreLanguagesPage'));
const ExploreStylesPage = lazy(() => import('./components/ExploreStylesPage'));
const ExploreMusicalsPage = lazy(() => import('./components/pages/ExploreMusicalsPage'));
const AffiliateProgramPage = lazy(() => import('./components/AffiliateProgramPage'));
const AffiliateRedirectPage = lazy(() => import('./components/AffiliateRedirectPage'));

const PUBLIC_FOOTER_PATHS = [
  '/gambit',
  '/custom-books',
  '/faq',
  '/blog',
  '/stories',
  '/story-details',
  '/narrators',
  '/musicals',
  '/styles',
  '/languages',
  '/morals',
  '/videos',
  '/privacy',
  '/terms-of-service',
  '/support',
  '/payment',
  '/create-story-books-with-ai',
  ...(WEB_RELEASE_FLAGS.affiliateRewardsUiEnabled ? ['/affiliate-program'] : []),
];

const shouldShowPublicFooter = (pathname: string) => {
  const parts = pathname.split('/').filter(Boolean);
  if (parts[0] && parts[0] in SUPPORTED_LANGUAGES) {
    parts.shift();
  }
  const normalizedPath = `/${parts.join('/')}`;
  if (isDashboardPath(normalizedPath)) return false;
  return normalizedPath === '/' || PUBLIC_FOOTER_PATHS.some((path) => normalizedPath === path || normalizedPath.startsWith(`${path}/`));
};

function App() {
  const location = useLocation();
  const restoreStudio = useRestoreStudioMode(STUDIO_POC_ENABLED);
  // Get all localized routes
  const localizedRoutes = getAllLocalizedRoutes();

  // Filter routes to only include home page variants (not video or styles)
  const homePageRoutes = localizedRoutes.filter(
    route => route.path !== '/videos' && !route.path.startsWith('/styles/')
  );

  const isStudio = STUDIO_POC_ENABLED && location.pathname === '/create/gambit';
  const isStoryForm = ['/create/detail', '/create/confirm'].includes(location.pathname)
    || location.pathname.startsWith('/create-next-chapter/');
  const isAccountBilling = ['/manage-subscription', '/subscription', '/buy-tokens'].includes(location.pathname);

  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <QueryProvider>
          <AuthProvider>
            <TermsAcceptanceGate>
            <RealtimeProvider>
              <WebSocketProviderV2>
            {/* Both modes share the account character library and its progress updates. */}
            {restoreStudio ? <Navigate to="/create/gambit" replace />
            : STUDIO_POC_ENABLED && location.pathname === '/create/studio' ? <LegacyStudioRedirect />
            : isStudio ? (
              <Suspense fallback={<StudioRouteLoading />}><StudioPage /></Suspense>
            ) : (
                <LanguageProvider>
                <DashboardShellProvider>
                <div className="App">
                  {/* SEO is handled per-page by each route component */}
                  <Header />
                  <RouteChangeTracker />
                  <AffiliateAttributionListener />
                  <DashboardContentFrame>
                  <Suspense fallback={location.pathname === '/create' ? <CreateStoryRouteLoading /> : isStoryForm ? <StoryFormLoading /> : <LoadingBookOverlay contained={isDashboardPath(location.pathname)} label={isAccountBilling ? 'Loading billing' : location.pathname === '/settings' ? 'Loading account' : 'Loading page'} />}>
                  <Routes>
                <Route path="/books" element={<Navigate to="/custom-books" replace />} />
                <Route path="/custom-books" element={<BooksPage />} />
                <Route path="/gambit" element={<GambitPage />} />
                {VoiceDesignPreview && <Route path="/design/voices" element={<VoiceDesignPreview />} />}
                {Object.keys(SUPPORTED_LANGUAGES).filter(lang => lang !== 'en').flatMap(lang => [
                  <Route key={`${lang}-gambit`} path={`/${lang}/gambit`} element={<GambitPage />} />,
                  <Route key={`${lang}-books`} path={`/${lang}/custom-books`} element={<BooksPage />} />,
                  <Route key={`${lang}-faq`} path={`/${lang}/faq`} element={<FAQ />} />,
                  <Route key={`${lang}-faq-detail`} path={`/${lang}/faq/:question`} element={<FAQQuestion />} />,
                  <Route key={`${lang}-blog`} path={`/${lang}/blog`} element={<BlogPage />} />,
                  <Route key={`${lang}-article`} path={`/${lang}/blog/:slug`} element={<BlogPostPage />} />,
                  <Route key={`${lang}-payment`} path={`/${lang}/payment`} element={<PaymentPage />} />,
                  <Route key={`${lang}-story-detail`} path={`/${lang}/story-details/:styleId/:storyName/:storyId`} element={<StoryDetailsPage />} />,
                  <Route key={`${lang}-story-detail-legacy`} path={`/${lang}/story-details/:styleId/:storyId`} element={<StoryDetailsPage />} />,
                ])}
                {/* Localized routes for all languages */}
                {homePageRoutes.map((route) => (
                  <Route key={route.fullPath} path={route.fullPath} element={route.path === '/ai-story-generator' ? <StoryGeneratorPage /> : <HomePage />} />
                ))}

                {/* English routes (default, no language prefix) */}
                {getAllRoutePaths()
                  .filter(path => path !== '/videos' && !path.startsWith('/styles/'))
                  .map((path) => (
                    <Route key={`en-${path}`} path={path} element={path === '/ai-story-generator' ? <StoryGeneratorPage /> : <HomePage />} />
                  ))}

                {/* Style routes - support both English and localized */}
                <Route path="/styles/:styleId" element={<StylePage />} />
                {Object.keys(SUPPORTED_LANGUAGES).map(lang => (
                  lang !== 'en' && (
                    <Route key={`${lang}-styles`} path={`/${lang}/styles/:styleId`} element={<StylePage />} />
                  )
                ))}

                {/* Narrator routes - support both English and localized */}
                <Route path="/narrators/:narratorId" element={<NarratorDetailPage />} />
                {Object.keys(SUPPORTED_LANGUAGES).map(lang => (
                  lang !== 'en' && (
                    <Route key={`${lang}-narrators`} path={`/${lang}/narrators/:narratorId`} element={<NarratorDetailPage />} />
                  )
                ))}

                {/* Language routes - support both English and localized */}
                <Route path="/languages/:languageId" element={<LanguageDetailPage />} />
                {Object.keys(SUPPORTED_LANGUAGES).map(lang => (
                  lang !== 'en' && (
                    <Route key={`${lang}-languages`} path={`/${lang}/languages/:languageId`} element={<LanguageDetailPage />} />
                  )
                ))}

                {/* Moral routes - support both English and localized */}
                <Route path="/morals/:moralId" element={<MoralDetailPage />} />
                {Object.keys(SUPPORTED_LANGUAGES).map(lang => (
                  lang !== 'en' && (
                    <Route key={`${lang}-morals`} path={`/${lang}/morals/:moralId`} element={<MoralDetailPage />} />
                  )
                ))}

                {/* Redirect old /video/ (singular) routes to /videos/ (plural) */}
                <Route path="/video/:narratorId" element={<LegacyVideoRedirect />} />
                <Route path="/video" element={<Navigate to="/videos" replace />} />
                {Object.keys(SUPPORTED_LANGUAGES).map(lang => (
                  lang !== 'en' && (
                    <React.Fragment key={`${lang}-video-redirect`}>
                      <Route path={`/${lang}/video/:narratorId`} element={<LegacyVideoRedirect language={lang} />} />
                      <Route path={`/${lang}/video`} element={<Navigate to={`/${lang}/videos`} replace />} />
                    </React.Fragment>
                  )
                ))}

                {/* Video page routes - support both English and localized */}
                <Route path="/videos/stories/:slug" element={<StoryVideoPage />} />
                <Route path="/videos/:narratorId" element={<VideoPage />} />
                <Route path="/videos/narrators" element={<VideoExplorePage />} />
                <Route path="/videos/stories" element={<VideoExplorePage />} />
                <Route path="/videos" element={<Navigate to="/videos/narrators" />} />
                {Object.keys(SUPPORTED_LANGUAGES).map(lang => (
                  lang !== 'en' && (
                    <React.Fragment key={`${lang}-video`}>
                      <Route path={`/${lang}/videos/stories/:slug`} element={<StoryVideoPage />} />
                      <Route path={`/${lang}/videos/:narratorId`} element={<VideoPage />} />
                      <Route path={`/${lang}/videos/narrators`} element={<VideoExplorePage />} />
                      <Route path={`/${lang}/videos/stories`} element={<VideoExplorePage />} />
                      <Route path={`/${lang}/videos`} element={<Navigate to={`/${lang}/videos/narrators`} />} />
                    </React.Fragment>
                  )
                ))}

                {/* Explore Stories page routes - support both English and localized */}
                <Route path="/stories" element={<ExploreStoriesPage />} />
                {Object.keys(SUPPORTED_LANGUAGES).map(lang => (
                  lang !== 'en' && (
                    <Route key={`${lang}-stories`} path={`/${lang}/stories`} element={<ExploreStoriesPage />} />
                  )
                ))}

                {/* Explore Narrators page routes - support both English and localized */}
                <Route path="/narrators" element={<ExploreNarratorsPage />} />
                {Object.keys(SUPPORTED_LANGUAGES).map(lang => (
                  lang !== 'en' && (
                    <Route key={`${lang}-explore-narrators`} path={`/${lang}/narrators`} element={<ExploreNarratorsPage />} />
                  )
                ))}

                {/* Explore Morals page routes - support both English and localized */}
                <Route path="/morals" element={<ExploreMoralsPage />} />
                {Object.keys(SUPPORTED_LANGUAGES).map(lang => (
                  lang !== 'en' && (
                    <Route key={`${lang}-explore-morals`} path={`/${lang}/morals`} element={<ExploreMoralsPage />} />
                  )
                ))}

                {/* Explore Languages page routes - support both English and localized */}
                <Route path="/languages" element={<ExploreLanguagesPage />} />
                {Object.keys(SUPPORTED_LANGUAGES).map(lang => (
                  lang !== 'en' && (
                    <Route key={`${lang}-explore-languages`} path={`/${lang}/languages`} element={<ExploreLanguagesPage />} />
                  )
                ))}

                {/* Explore Styles page routes - support both English and localized */}
                <Route path="/styles" element={<ExploreStylesPage />} />
                {Object.keys(SUPPORTED_LANGUAGES).map(lang => (
                  lang !== 'en' && (
                    <Route key={`${lang}-explore-styles`} path={`/${lang}/styles`} element={<ExploreStylesPage />} />
                  )
                ))}

                {/* Explore Musicals page routes - support both English and localized */}
                <Route path="/musicals" element={<ExploreMusicalsPage />} />
                {Object.keys(SUPPORTED_LANGUAGES).map(lang => (
                  lang !== 'en' && (
                    <Route key={`${lang}-explore-musicals`} path={`/${lang}/musicals`} element={<ExploreMusicalsPage />} />
                  )
                ))}

                {/* Musical Details routes - SEO-friendly format */}
                {/* Redirect old story-details/musical format to new /musicals/ format */}
                <Route path="/story-details/musical/:storyName/:storyId" element={<LegacyMusicalRedirect />} />
                <Route path="/story-details/musical/:storyId" element={<LegacyMusicalRedirect />} />

                {/* New format with story name */}
                <Route path="/musicals/:storyName/:storyId" element={<StoryDetailsPage />} />
                {/* Old format for backward compatibility */}
                <Route path="/musicals/:storyId" element={<StoryDetailsPage />} />
                {Object.keys(SUPPORTED_LANGUAGES).map(lang => (
                  lang !== 'en' && (
                    <React.Fragment key={`${lang}-musical-details`}>
                      <Route path={`/${lang}/story-details/musical/:storyName/:storyId`} element={<LegacyMusicalRedirect language={lang} />} />
                      <Route path={`/${lang}/story-details/musical/:storyId`} element={<LegacyMusicalRedirect language={lang} />} />
                      <Route path={`/${lang}/musicals/:storyName/:storyId`} element={<StoryDetailsPage />} />
                      <Route path={`/${lang}/musicals/:storyId`} element={<StoryDetailsPage />} />
                    </React.Fragment>
                  )
                ))}

                {/* Auth callback route */}
                <Route path="/auth/callback" element={<AuthCallback />} />

                {/* TikTok auth callback route */}
                <Route path="/auth/tiktok/callback" element={<TikTokAuthCallback />} />

                {/* Payment callback route - redirects back to app after Stripe checkout */}
                <Route path="/payment/callback" element={<PaymentCallback />} />

                {/* Social OAuth callback route - redirects back to app after account connection */}
                <Route path="/social/callback" element={<SocialCallback />} />

                {/* App deep link route - universal link fallback for users without app installed */}
                <Route path="/app" element={<PaymentCallback />} />
                <Route path="/app/*" element={<PaymentCallback />} />

                {/* Sign In route */}
                <Route path="/signin" element={<SignInPage />} />

                {/* My Stories route */}
                <Route path="/my-stories" element={<MyStories />} />
                <Route path="/create" element={<CreateStoryPage />} />
                <Route path="/create/detail" element={<CreateStoryDetailPage />} />
                <Route path="/create/confirm" element={<CreateStoryDetailPage />} />
                <Route path="/create-next-chapter/:storyId" element={<CreateNextChapterPage />} />
                <Route path="/story-preview/build/:chapterId" element={<Suspense fallback={null}><StudioPendingStoryPreview /></Suspense>} />
                <Route path="/story-preview/:storyId" element={<StoryPreviewPage />} />
                <Route path="/characters" element={<CharactersPage />} />
                <Route path="/characters/new" element={<CreateCharacterPage />} />
                <Route path="/characters/:characterId" element={<CreateCharacterPage />} />
                <Route path="/voices" element={<VoicesPage />} />
                <Route path="/voices/new" element={<CreateVoicePage />} />
                <Route path="/voices/:voiceId" element={<CreateVoicePage />} />
                <Route path="/share" element={<ShareDetailsPage />} />
                <Route path="/download" element={<DownloadPage />} />

                {/* Settings route */}
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/usage" element={<UsagePage />} />
                <Route path="/usage/how-tokens-work" element={<Navigate to="/usage?view=pricing" replace />} />
                <Route path="/social-accounts" element={<SocialAccountsPage />} />
                <Route path="/publishing-results" element={<PublishingResultsPage />} />
                <Route
                  path="/affiliate-program"
                  element={
                    WEB_RELEASE_FLAGS.affiliateRewardsUiEnabled
                      ? <AffiliateProgramPage />
                      : <Navigate to="/" replace />
                  }
                />
                <Route path="/r/:referralCode" element={<AffiliateRedirectPage />} />

                {/* Book Builder route */}
                <Route path="/book-builder/*" element={<BookBuilder />} />
                <Route path="/book-preview" element={<BookPreview />} />

                {/* My Orders routes */}
                <Route path="/my-orders" element={<MyOrders />} />
                <Route path="/my-orders/:orderId" element={<OrderDetail />} />

                {/* Notifications route */}
                <Route path="/notifications" element={<NotificationsPage />} />

                {/* Support route */}
                <Route path="/support" element={<SupportPage />} />

                {/* Payment route */}
                <Route path="/payment" element={<PaymentPage />} />
                <Route path="/buy-tokens" element={<AccountBillingPage view="tokens" />} />
                <Route path="/manage-subscription" element={<AccountBillingPage view="subscription" />} />
                <Route path="/subscription" element={<Navigate to="/manage-subscription" replace />} />
                <Route path="/tokens" element={<Navigate to="/buy-tokens" replace />} />

                {/* Blog routes */}
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/blog/:slug" element={<BlogPostPage />} />

                {/* Edit Story route */}
                <Route path="/edit-story/:storyId" element={<EditStory />} />

                {/* Story Details routes - placed before generic story routes */}
                {/* New format with story name */}
                <Route path="/story-details/:styleId/:storyName/:storyId" element={<StoryDetailsPage />} />
                {/* Old format for backward compatibility */}
                <Route path="/story-details/:styleId/:storyId" element={<StoryDetailsPage />} />

                {/* Existing routes */}
                <Route path="/story" element={<StoryScreen />} />
                <Route path="/story/:storyName/:storyId" element={<StoryScreen />} />
                {/* Legacy title-only and query-parameter story links */}
                <Route path="/story/:storyName" element={<StoryScreen />} />
                <Route path="/delete-profile" element={<DeleteProfile />} />
                <Route path="/delete-profile-confirm" element={<DeleteConfirm />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms-of-service" element={<Terms />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/faq/:question" element={<FAQQuestion />} />
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                  </Suspense>
                  {shouldShowPublicFooter(location.pathname) && <PublicFooter />}
                  </DashboardContentFrame>
                </div>
                </DashboardShellProvider>
                </LanguageProvider>
            )}
              </WebSocketProviderV2>
            </RealtimeProvider>
            </TermsAcceptanceGate>
          </AuthProvider>
        </QueryProvider>
      </PersistGate>
    </Provider>
  );
}

export default App;
